import axios from "axios";
import { extractErrorMessage } from "@/lib/errorHandler";

const API_BASE = "/api"

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: false,
  timeout: 30000,
});

/**
 * Check if an upload response indicates success
 * Treats response as success if:
 * - HTTP status is 200-299 (axios resolves for these)
 * - response.data contains a path/url/file_path field
 * - response.data.message includes "uploaded" or "success"
 * - response.data.success === true
 */
const isUploadSuccess = (response) => {
  if (!response) {
    console.log('[API] isUploadSuccess: No response');
    return false;
  }
  
  const status = response.status;
  const data = response.data;
  
  console.log('[API] isUploadSuccess checking:', { status, data });
  
  // HTTP status 200-299 = SUCCESS (axios only resolves for these)
  if (status >= 200 && status < 300) {
    console.log('[API] isUploadSuccess: HTTP 2xx = SUCCESS');
    return true;
  }
  
  // Explicit success flag
  if (data?.success === true) {
    console.log('[API] isUploadSuccess: data.success === true');
    return true;
  }
  
  // Check for file path/URL in response
  if (data?.path || data?.url || data?.file_path || data?.file_url) {
    console.log('[API] isUploadSuccess: Found path/url in response');
    return true;
  }
  
  // Check for success message
  if (typeof data?.message === 'string') {
    const msg = data.message.toLowerCase();
    if (msg.includes('uploaded') || msg.includes('success')) {
      console.log('[API] isUploadSuccess: Message contains uploaded/success');
      return true;
    }
  }
  
  console.log('[API] isUploadSuccess: No success indicators found');
  return false;
};

/**
 * Perform a file upload with robust success detection
 * Returns { success: boolean, data: any, error?: string }
 */
const performUpload = async (url, formData, method = 'post') => {
  console.log(`[API] ===== UPLOAD STARTING =====`);
  console.log(`[API] Method: ${method.toUpperCase()}`);
  console.log(`[API] URL: ${url}`);
  
  try {
    const response = method === 'put' 
      ? await api.put(url, formData)
      : await api.post(url, formData);
    
    console.log('[API] ===== UPLOAD RESPONSE RECEIVED =====');
    console.log('[API] Status:', response.status);
    console.log('[API] StatusText:', response.statusText);
    console.log('[API] Data:', JSON.stringify(response.data, null, 2));
    
    // For 2xx responses, ALWAYS treat as success
    if (response.status >= 200 && response.status < 300) {
      console.log('[API] ===== UPLOAD SUCCESS (HTTP 2xx) =====');
      return { success: true, data: response.data };
    }
    
    // Check using isUploadSuccess for other cases
    if (isUploadSuccess(response)) {
      console.log('[API] ===== UPLOAD SUCCESS (isUploadSuccess) =====');
      return { success: true, data: response.data };
    }
    
    // Response received but doesn't look like success - still return success for 2xx
    console.warn('[API] Unexpected response format but treating as success:', response.data);
    return { success: true, data: response.data };
    
  } catch (error) {
    // Log full error details
    console.error('[API] ===== UPLOAD ERROR =====');
    console.error('[API] Error name:', error.name);
    console.error('[API] Error message:', error.message);
    console.error('[API] Error code:', error.code);
    console.error('[API] Response status:', error.response?.status);
    console.error('[API] Response statusText:', error.response?.statusText);
    console.error('[API] Response data:', error.response?.data);
    console.error('[API] Full error object:', error);
    
    const errorMessage = extractErrorMessage(error, "Upload failed");
    
    return { success: false, data: null, error: errorMessage };
  }
};

// Add auth token to requests and handle FormData Content-Type
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Remove Content-Type for FormData to let browser set it with boundary
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  
  return config;
});

// ───────────────────────────── Error normalization ─────────────────────────────
//
// Every request that fails passes through a single response interceptor. The
// interceptor wraps the raw Axios error into a `NormalizedApiError` so callers
// can always safely do:
//
//     try { … }
//     catch (err) { toast.error(err.message); }
//
// without ever having to remember to sanitise Pydantic 422 arrays,
// blob payloads, network errors, etc. `err.message` is guaranteed to be a
// human-readable, non-empty string; `err.status`, `err.code` and `err.original`
// expose the raw metadata for the rare caller that needs it.

export class NormalizedApiError extends Error {
  /**
   * @param {object}  opts
   * @param {string}  opts.message   - Human-readable, safe-to-render string.
   * @param {number}  opts.status    - HTTP status code (0 for network/timeout).
   * @param {string}  opts.code      - Error code (axios `err.code` OR body `code`, else "").
   * @param {*}       opts.original  - The raw AxiosError, kept for debug/DevTools.
   */
  constructor({ message, status, code, original }) {
    super(message || "Something went wrong");
    this.name = "NormalizedApiError";
    this.status = status || 0;
    this.code = code || "";
    this.original = original;
    // Preserve the axios shape too so any existing helper that reads
    // `err.response?.data?.detail` keeps working during the migration.
    if (original) {
      this.response = original.response;
      this.config = original.config;
      this.request = original.request;
    }
  }
}

/**
 * Extract a safe, human-readable string from ANY error body shape.
 * Handles:
 *   • FastAPI 422       → array of {type, loc, msg, input}
 *   • FastAPI plain     → {detail: "…"}
 *   • {message}/{error} → common non-FastAPI shapes
 *   • Nested {msg}      → single Pydantic error object
 *   • Non-string leaves → JSON.stringify (capped 400 chars)
 *
 * NEVER returns undefined/null/object. Always a string.
 */
function _extractErrorText(error) {
  const raw =
    error?.response?.data?.detail ??
    error?.response?.data?.message ??
    error?.response?.data?.error ??
    (typeof error?.response?.data === "string" ? error.response.data : null);

  if (raw == null || raw === "") return "";
  if (typeof raw === "string") return raw.slice(0, 400);
  if (Array.isArray(raw)) {
    const parts = raw
      .map((e) => {
        if (typeof e === "string") return e;
        if (!e || typeof e !== "object") return String(e);
        const loc = Array.isArray(e.loc) ? e.loc.slice(-2).join(".") : "";
        const msg = e.msg || e.message || "Invalid input";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    return parts.join("; ").slice(0, 400);
  }
  if (typeof raw === "object") {
    if (raw.msg || raw.message) return String(raw.msg || raw.message).slice(0, 400);
    try { return JSON.stringify(raw).slice(0, 400); } catch { return ""; }
  }
  return String(raw).slice(0, 400);
}

/**
 * Fallback messages keyed on the HTTP status code. Used only when the response
 * body doesn't carry a specific message (or is a Blob we can't read yet).
 * Deliberately generic — callers can still override for context-specific
 * wording via `err.message || "custom"`.
 */
const _STATUS_DEFAULTS = {
  0:   "Network error — check your connection",
  400: "Bad request",
  401: "Session expired — please log in again",
  403: "You don't have permission for this action",
  404: "Not found",
  409: "Conflict — this record was modified elsewhere",
  413: "File too large",
  415: "Unsupported file type",
  422: "Some fields are invalid",
  429: "Too many requests — slow down",
  500: "Server error — please try again",
  502: "Server is warming up — please try again",
  503: "Service temporarily unavailable",
  504: "Server timed out — please try again",
};

/** Build a NormalizedApiError from any Axios error. */
function _normalizeAxiosError(error) {
  const status = error?.response?.status || 0;
  const isTimeout = error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "");
  const isNetwork = !error?.response && !isTimeout;

  // 1) Body-provided text always wins (most specific).
  let message = _extractErrorText(error);

  // 2) Fall back to a friendly status-based default.
  if (!message) {
    if (isTimeout) message = "Request timed out — please try again";
    else if (isNetwork) message = _STATUS_DEFAULTS[0];
    else message = _STATUS_DEFAULTS[status] || `Request failed (HTTP ${status})`;
  }

  const code =
    error?.response?.data?.code ||
    error?.response?.data?.error_code ||
    error?.code ||
    (isTimeout ? "TIMEOUT" : isNetwork ? "NETWORK" : `HTTP_${status}`);

  return new NormalizedApiError({ message, status, code, original: error });
}
// ─────────────────────────── /Error normalization ────────────────────────────


// Response interceptor — the single source of truth for API error handling.
//
// Every non-2xx response is transformed into a `NormalizedApiError` so that
// components can consume `err.message` (guaranteed safe string), `err.status`,
// `err.code` and `err.original` without any per-callsite sanitisation.
// This is the architectural safety net that eliminates the entire class of
// "Objects are not valid as a React child" white-screen bugs caused by
// passing raw Pydantic 422 arrays or blob payloads into `toast.error()`.
api.interceptors.response.use(
  (response) => {
    // Log successful responses for uploads
    if (response.config?.data instanceof FormData) {
      console.log('[API] FormData response received:', response.status, response.data);
    }
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url;

    // Blob-typed error bodies (from `responseType: "blob"` requests such as
    // Excel/PDF exports) hide the JSON error detail inside the Blob. Read it
    // synchronously into `.data` so `_extractErrorText()` can see it.
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        try { error.response.data = JSON.parse(text); }
        catch { error.response.data = { detail: text }; }
      } catch { /* keep Blob if reading fails */ }
    }

    // Handle 401 for session expiry (not login attempts). Runs BEFORE
    // normalization so the login redirect logic can still read the raw body.
    if (status === 401 && !url?.includes("/auth/login")) {
      const rawDetail = error.response?.data?.detail;
      const detailStr = typeof rawDetail === "string" ? rawDetail : "";
      const isAnotherDevice = detailStr.includes("another device");

      localStorage.removeItem("token");
      localStorage.removeItem("user");

      if (isAnotherDevice) {
        // Store message for login page to display
        localStorage.setItem("session_kicked", "You have been logged out due to login from another device");
      }

      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }

    return Promise.reject(_normalizeAxiosError(error));
  }
);

// Auth API
// SEC-002: `register()` client fn removed — public self-registration is
// disabled server-side and no UI calls it. Admins provision users via
// `createUser()` → POST /auth/create-user.
export const login = (data) => api.post("/auth/login", data);
export const getMe = () => api.get("/auth/me");
export const createUser = (data) => api.post("/auth/create-user", data);
export const listUsers = () => api.get("/auth/users");
export const deactivateUser = (userId) => api.put(`/auth/users/${userId}/deactivate`);
export const resetUserPassword = (userId) => api.put(`/auth/users/${userId}/reset-password`);
export const requestPasswordOTP = (data) => api.post("/auth/change-password/request-otp", data);
export const verifyOTPChangePassword = (data) => api.post("/auth/change-password/verify-otp", data);

// Lot Mapping & Client Dashboard (Admin Only)
export const getLotMappingOptions = () => api.get("/lot-mappings/options");
export const getLotMappings = () => api.get("/lot-mappings");
export const updateLotMapping = (lotId, data) => api.put(`/lot-mappings/${lotId}`, data);
export const getClients = (params) => api.get("/clients", { params });
// Picker list calls use a tighter timeout so the UI fails fast (Retry button)
// instead of spinning for 30s on hung/404 endpoints in production.
const PICKER_TIMEOUT = 10000;
export const getClientList = () => api.get("/clients/list", { timeout: PICKER_TIMEOUT });
export const createClient = (name) => api.post("/clients", { name });
export const getWeaverList = () => api.get("/weavers", { timeout: PICKER_TIMEOUT });
export const createWeaver = (name) => api.post("/weavers", { name });
export const getClientDashboard = (partyName, params) =>
  api.get(`/clients/${encodeURIComponent(partyName)}/dashboard`, { params });
export const getBusinessInsights = (params) =>
  api.get("/dashboard/business-insights", { params });
export const mergeClients = (sourcePartyName, targetPartyName) =>
  api.post("/clients/merge", { source_party_name: sourcePartyName, target_party_name: targetPartyName });
export const getMergeHistory = () => api.get("/clients/merge-history");

// Dashboard exports (PDF + Excel). Filters apply to ClientDashboard.
// NOTE: paths use /export/xlsx and /export/pdf (no dots) — some ingress/CDN configs
// treat dotted paths as static assets and route them incorrectly.
export const exportClientDashboardExcel = (partyName, params) =>
  api.get(`/clients/${encodeURIComponent(partyName)}/dashboard/export/xlsx`, { params, responseType: "blob" });
export const exportClientDashboardPdf = (partyName, params) =>
  api.get(`/clients/${encodeURIComponent(partyName)}/dashboard/export/pdf`, { params, responseType: "blob" });
export const exportBusinessDashboardExcel = (params) =>
  api.get("/dashboard/business-insights/export/xlsx", { params, responseType: "blob" });
export const exportBusinessDashboardPdf = (params) =>
  api.get("/dashboard/business-insights/export/pdf", { params, responseType: "blob" });

// Trigger a browser download from a blob axios response.
export const triggerBlobDownload = (response, fallbackFilename) => {
  const blob = response.data;
  // Try to extract filename from Content-Disposition
  const disp = response.headers?.["content-disposition"] || "";
  const match = /filename="?([^";]+)"?/i.exec(disp);
  const filename = match?.[1] || fallbackFilename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

// Lots API
export const getLots = () => api.get("/lots");
export const getAvailableBatchesSummary = () => api.get("/lots/available-batches-summary");
export const getLot = (lotId) => api.get(`/lots/${lotId}`);
export const createLot = (data) => api.post("/lots", data);
export const updateLot = (lotId, data) => api.put(`/lots/${lotId}`, data);
export const archiveLot = (lotId) => api.post(`/lots/${lotId}/archive`);
export const unarchiveLot = (lotId) => api.post(`/lots/${lotId}/unarchive`);
export const deleteLot = (lotId) => api.delete(`/lots/${lotId}`);

// Rolls API
export const getRolls = (lotId, status) => {
  const params = status ? { status } : {};
  return api.get(`/lots/${lotId}/rolls`, { params });
};
// getBatches with cache-busting to ensure fresh data
// dispatchableOnly=true → hide batches with zero available rolls (Dispatch page)
// dispatchableOnly=false (default) → return full batch history (Lot Detail page)
export const getBatches = (lotId, { dispatchableOnly = false } = {}) => api.get(`/lots/${lotId}/batches`, {
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
  params: {
    _t: Date.now(), // Cache buster
    ...(dispatchableOnly ? { dispatchable_only: true } : {})
  }
});
export const createRoll = (lotId, data) => api.post(`/lots/${lotId}/rolls`, data);
export const updateRoll = (lotId, rollId, data) => api.put(`/lots/${lotId}/rolls/${rollId}`, data);
export const cancelRoll = (lotId, rollId, reason) => api.post(`/lots/${lotId}/rolls/${rollId}/cancel`, { cancellation_reason: reason });
export const deleteRoll = (lotId, rollId) => api.delete(`/lots/${lotId}/rolls/${rollId}`);

// Bulk Operations API
export const bulkDispatch = (data) => api.post("/bulk/dispatch", data);
export const bulkUpdateRolls = (lotId, data) => api.put(`/lots/${lotId}/bulk/update`, data);
export const bulkDeleteRolls = (lotId, rollIds) => {
  const params = new URLSearchParams();
  rollIds.forEach(id => params.append('roll_ids', id));
  return api.delete(`/lots/${lotId}/bulk/delete?${params.toString()}`);
};

// Dispatch API
export const createDispatch = (data) => api.post("/dispatch", data);
// Multi-lot dispatch — one dispatch record containing rolls from N lots.
// Payload shape: { selections: [{lot_id, roll_ids}], party_name, challan_no, sale_rate, dispatch_date? }
export const createMultiLotDispatch = (data) => api.post("/dispatch/multi-lot", data);
export const getDispatches = (params) => api.get("/dispatches", { params });
export const getDispatch = (dispatchId) => api.get(`/dispatches/${dispatchId}`);
export const updateDispatch = (dispatchId, data) => api.put(`/dispatches/${dispatchId}`, data);
export const cancelDispatch = (dispatchId, reason) => api.post(`/dispatches/${dispatchId}/cancel`, null, { params: { reason } });
export const getDispatchesForReturn = () => api.get("/dispatches/for-return");
export const getDispatchRollsForReturn = (dispatchId) => api.get(`/dispatches/${dispatchId}/rolls-for-return`);

// Sale Rate backfill (Admin)
export const getDispatchesMissingSaleRate = () => api.get("/dispatches/missing-sale-rate");
export const bulkUpdateSaleRate = (updates) => api.post("/dispatches/bulk-update-sale-rate", { updates });

// AI Business Advisor
export const generateAIInsights = () => api.post("/ai-advisor/insights", {}, { timeout: 180000 });
export const askAIChat = (message, sessionId) =>
  api.post("/ai-advisor/chat", { message, session_id: sessionId }, { timeout: 120000 });
// 2-step CEO Report flow: first fetch the JSON (Gemini, ~40s), then render to PDF (instant).
// This splits the work so neither leg hits the 60s ingress timeout.
export const previewWeeklyCEOReport = () =>
  api.post("/ai-advisor/weekly-ceo-report", {}, { timeout: 180000 });
export const renderWeeklyCEOReportPdf = (payload) =>
  api.post("/ai-advisor/weekly-ceo-report/render-pdf", payload, {
    responseType: "blob",
    timeout: 60000,
  });

// Lot Cost & Profitability (Admin-only — backend enforces 403 for non-admins)
export const getLotCost = (lotId) => api.get(`/lots/${lotId}/cost`);
export const upsertLotCost = (lotId, payload) => api.put(`/lots/${lotId}/cost`, payload);
export const getBusinessProfitability = (params) => api.get("/dashboard/profitability", { params });
// Price Bands (Phase 2)
export const listPriceBands = () => api.get("/price-bands");
export const createPriceBand = (data) => api.post("/price-bands", data);
export const updatePriceBand = (id, data) => api.put(`/price-bands/${id}`, data);
export const deletePriceBand = (id) => api.delete(`/price-bands/${id}`);
export const reorderPriceBands = (order) => api.post("/price-bands/reorder", { order });
export const auditPriceBands = () => api.get("/price-bands/audit");

// Price Analytics (Phase 2)
export const getClientPriceAnalytics = (partyName, params) =>
  api.get(`/clients/${encodeURIComponent(partyName)}/price-analytics`, { params });
export const getBusinessPriceAnalytics = (params) =>
  api.get(`/dashboard/business-price-analytics`, { params });
export const getLotAnalytics = (lotId) => api.get(`/lots/${lotId}/analytics`);
export const getLotsAvgSaleRate = () => api.get(`/lots-avg-sale-rate`);

// Goods Return API
export const getGoodsReturns = (params) => api.get("/goods-returns", { params });
export const getGoodsReturn = (returnId) => api.get(`/goods-returns/${returnId}`);
export const createGoodsReturn = (data) => api.post("/goods-returns", data);
export const updateGoodsReturn = (returnId, data) => api.put(`/goods-returns/${returnId}`, data);
export const cancelGoodsReturn = (returnId, reason) => api.post(`/goods-returns/${returnId}/cancel`, null, { params: { reason } });
export const deleteGoodsReturn = (returnId) => api.delete(`/goods-returns/${returnId}`);
export const approveGoodsReturn = (returnId) => api.post(`/goods-returns/${returnId}/approve`);
export const rejectGoodsReturn = (returnId, reason) => api.post(`/goods-returns/${returnId}/reject`, { reason });
export const getGoodsReturnApprovalCounts = () => api.get("/goods-returns/approval-counts");

// Damage Register API
export const getDamageRegister = (params) => api.get("/damage-register", { params });
export const getDamageSummary = (params) => api.get("/damage-register/summary", { params });
export const getDamageAnalytics = (params) => api.get("/damage-register/analytics", { params });
export const createMeterShortage = (data) => api.post("/damage-register/meter-shortage", data);
export const createCustomerClaim = (data) => api.post("/damage-register/customer-claim", data);
export const updateDamageRecord = (id, data) => api.patch(`/damage-register/${id}`, data);
export const approveDamageRecord = (id) => api.post(`/damage-register/${id}/approve`);
export const rejectDamageRecord = (id, reason) => api.post(`/damage-register/${id}/reject`, null, { params: { reason } });
export const exportDamageXlsx = (params) => api.get("/damage-register/export.xlsx", { params, responseType: "blob", timeout: 90000 });
export const exportDamagePdf = (params) => api.get("/damage-register/export.pdf", { params, responseType: "blob", timeout: 90000 });

// Stock Transfer API
export const getRollsByLocation = (location, lotId) => api.get("/rolls/by-location", { params: { location, lot_id: lotId } });
export const getLotsWithRollsAtLocation = (location) => api.get("/lots/with-rolls-at-location", { params: { location } });
export const createStockTransfer = (data) => api.post("/stock-transfers", data);
export const createBulkStockTransfer = (rollIds, fromLocation, toLocation, transferDate, notes) => 
  api.post("/stock-transfers/bulk", null, { params: { roll_ids: rollIds, from_location: fromLocation, to_location: toLocation, transfer_date: transferDate, notes } });
export const getStockTransfers = (params) => api.get("/stock-transfers", { params });
export const updateStockTransfer = (transferId, data) => api.put(`/stock-transfers/${transferId}`, data);
export const cancelStockTransfer = (transferId, reason) => api.post(`/stock-transfers/${transferId}/cancel`, null, { params: { reason } });
export const deleteStockTransfer = (transferId) => api.delete(`/stock-transfers/${transferId}`);
export const getInventoryLocationSummary = () => api.get("/inventory/location-summary");

// Roll Inventory API
export const getRollInventory = (params) => api.get("/rolls/inventory", { params });
export const getRollInventorySummary = () => api.get("/rolls/inventory/summary");
export const getInventoryReconciliation = () => api.get("/inventory/reconciliation");

// Dispatch Consistency Check API (Admin only)
export const getLatestConsistencyCheck = () => api.get("/dispatch-consistency/latest");
export const runConsistencyCheck = (autoFix = true) => api.post(`/dispatch-consistency/run?auto_fix=${autoFix}`);

// Database Consistency API (Admin only)
export const getDatabaseConsistencyCheck = () => api.get("/database/consistency-check");
export const repairDatabase = (params) => api.post("/database/repair", null, { params });

// Inventory Adjustment API
export const getInventoryAdjustments = (params) => api.get("/inventory-adjustments", { params });
export const createInventoryAdjustment = (data) => api.post("/inventory-adjustments", data);
// Split one roll into N children — parent gets soft-cancelled, children inherit
// lot/colour/location, meters must sum to parent within tolerance.
export const splitRollAdjustment = (data) => api.post("/inventory-adjustments/split-roll", data);
export const getInventoryAdjustment = (adjustmentId) => api.get(`/inventory-adjustments/${adjustmentId}`);

// Excel API
export const getExcelColumns = (file, sheetName = null) => {
  const formData = new FormData();
  formData.append("file", file);
  let url = `/excel/columns`;
  if (sheetName) {
    url += `?sheet_name=${encodeURIComponent(sheetName)}`;
  }
  // Let axios set Content-Type automatically with proper boundary
  return api.post(url, formData);
};

export const previewExcel = (file, lotNo, sheetName = null, columnMapping = null) => {
  const formData = new FormData();
  formData.append("file", file);
  let url = `/excel/preview?lot_no=${encodeURIComponent(lotNo)}`;
  if (sheetName) {
    url += `&sheet_name=${encodeURIComponent(sheetName)}`;
  }
  if (columnMapping) {
    if (columnMapping.roll_no_column) {
      url += `&roll_no_column=${encodeURIComponent(columnMapping.roll_no_column)}`;
    }
    if (columnMapping.meter_column) {
      url += `&meter_column=${encodeURIComponent(columnMapping.meter_column)}`;
    }
    if (columnMapping.colour_column) {
      url += `&colour_column=${encodeURIComponent(columnMapping.colour_column)}`;
    }
  }
  // Let axios set Content-Type automatically with proper boundary
  return api.post(url, formData);
};
export const autoBatchRolls = (rows) => api.post("/excel/auto-batch", { rows });
export const importExcel = (data) => api.post("/excel/import", data);

// Dashboard API
export const getDashboardStats = () => api.get("/dashboard/stats");

// Audit Logs API
export const getAuditLogs = (params) => api.get("/audit-logs", { params });
// Paged variant used by the Audit Logs page. Returns `{items, total, offset, limit}`.
export const getAuditLogsPaged = (params) =>
  api.get("/audit-logs", { params: { paged: true, ...params } });
export const getAuditLogUsers = () => api.get("/audit-logs/users");

// Export API - Download functions with authentication
export const downloadLotExcel = async (lotId) => {
  const response = await api.get(`/export/lot/${lotId}/excel`, {
    responseType: 'blob'
  });
  return response;
};

export const downloadLotPdf = async (lotId) => {
  const response = await api.get(`/export/lot/${lotId}/pdf`, {
    responseType: 'blob'
  });
  return response;
};

export const downloadDispatchPdf = async (dispatchId) => {
  const response = await api.get(`/export/dispatch/${dispatchId}/pdf`, {
    responseType: 'blob'
  });
  return response;
};

export const downloadRollInventoryExcel = async (params) => {
  return api.get("/export/rolls/inventory/excel", { params, responseType: "blob" });
};

export const downloadRollInventoryPdf = async (params) => {
  return api.get("/export/rolls/inventory/pdf", { params, responseType: "blob" });
};

export const downloadDispatchHistoryExcel = async (params) => {
  return api.get("/export/dispatches/excel", { params, responseType: "blob" });
};

export const downloadDispatchHistoryPdf = async (params) => {
  return api.get("/export/dispatches/pdf", { params, responseType: "blob" });
};

export const downloadLotsExcel = async (params) => {
  return api.get("/export/lots/excel", { params, responseType: "blob" });
};

export const downloadLotsPdf = async (params) => {
  return api.get("/export/lots/pdf", { params, responseType: "blob" });
};

export const downloadGoodsReturnsExcel = async (params) => {
  return api.get("/export/goods-returns/excel", { params, responseType: "blob" });
};

export const downloadGoodsReturnsPdf = async (params) => {
  return api.get("/export/goods-returns/pdf", { params, responseType: "blob" });
};

// Legacy URL getters (deprecated - use download functions instead)
export const getExportLotExcelUrl = (lotId) => `${API_BASE}/export/lot/${lotId}/excel`;
export const getExportLotPdfUrl = (lotId) => `${API_BASE}/export/lot/${lotId}/pdf`;
export const getExportDispatchPdfUrl = (dispatchId) => `${API_BASE}/export/dispatch/${dispatchId}/pdf`;

// ===================== PRODUCTION MODULE API =====================

// Dashboard Stats
export const getProductionDashboardStats = (params) => api.get("/production/dashboard-stats", { params });

// Greige Stock
export const getGreigeStock = (params) => api.get("/production/greige-stock", { params });
export const getGreigeStockFilters = () => api.get("/production/greige-stock/filters");
export const createGreigeStock = (data) => api.post("/production/greige-stock", data);
export const updateGreigeStock = (id, data) => api.put(`/production/greige-stock/${id}`, data);

// Factories
export const getFactories = () => api.get("/production/factories");
export const createFactory = (data) => api.post("/production/factories", data);
export const updateFactory = (id, data) => api.put(`/production/factories/${id}`, data);

// Production Lots
export const getProductionLots = (params) => api.get("/production/lots", { 
  params: { ...params, _t: Date.now() },
  headers: { "Cache-Control": "no-cache, no-store, must-revalidate", "Pragma": "no-cache" }
});
export const getProductionLot = (id) => api.get(`/production/lots/${id}`, {
  params: { _t: Date.now() },
  headers: { "Cache-Control": "no-cache, no-store, must-revalidate", "Pragma": "no-cache" }
});
export const createProductionLot = (data) => api.post("/production/lots", data);

export const uploadGreigeImage = async (lotId, file) => {
  console.log('[API] uploadGreigeImage - Starting:', { lotId, fileName: file?.name, fileSize: file?.size });
  const formData = new FormData();
  formData.append("file", file);
  
  const result = await performUpload(`/production/lots/${lotId}/upload-greige-image`, formData, 'post');
  
  if (result.success) {
    console.log('[API] uploadGreigeImage - SUCCESS:', result.data);
    return { data: result.data, status: 200 };
  } else {
    console.error('[API] uploadGreigeImage - FAILED:', result.error);
    const error = new Error(result.error);
    error.response = { data: { detail: result.error } };
    throw error;
  }
};

export const updateGreigeImage = async (lotId, file) => {
  console.log('[API] updateGreigeImage - Starting:', { lotId, fileName: file?.name, fileSize: file?.size });
  const formData = new FormData();
  formData.append("file", file);
  
  const result = await performUpload(`/production/lots/${lotId}/greige-image`, formData, 'put');
  
  if (result.success) {
    console.log('[API] updateGreigeImage - SUCCESS:', result.data);
    return { data: result.data, status: 200 };
  } else {
    console.error('[API] updateGreigeImage - FAILED:', result.error);
    const error = new Error(result.error);
    error.response = { data: { detail: result.error } };
    throw error;
  }
};
export const cancelProductionLot = (id, reason) => api.put(`/production/lots/${id}/cancel`, { reason });
export const assignLotNumber = (id, lotNumber) => api.put(`/production/lots/${id}/assign-lot-number`, { lot_number: lotNumber });
export const markLotReady = (id) => api.post(`/production/lots/${id}/mark-ready`);

// Club / Unclub Production Lots
export const clubProductionLots = (leadId, memberIds) =>
  api.post("/production-lots/club", { lead_id: leadId, member_ids: memberIds });
export const unclubProductionLots = (leadId) =>
  api.post(`/production-lots/${leadId}/unclub`);

// Production Stages
export const addProductionStage = (data) => api.post("/production/stages", data);
export const getProductionStagesList = () => api.get("/production/stages/list");
export const updateProductionStage = (stageId, data) => api.put(`/production/stages/${stageId}`, data);
export const deleteProductionStage = (stageId) => api.delete(`/production/stages/${stageId}`);

// Fabric Completions
export const addFabricCompletion = (data) => api.post("/production/completions", data);
export const updateFabricCompletion = (completionId, data) => api.put(`/production/completions/${completionId}`, data);
export const deleteFabricCompletion = (completionId) => api.delete(`/production/completions/${completionId}`);

export const uploadCompletionImage = async (completionId, file) => {
  console.log('[API] uploadCompletionImage - Starting:', { completionId, fileName: file?.name, fileSize: file?.size });
  const formData = new FormData();
  formData.append("file", file);
  
  const result = await performUpload(`/production/completions/${completionId}/upload-image`, formData, 'post');
  
  if (result.success) {
    console.log('[API] uploadCompletionImage - SUCCESS:', result.data);
    return { data: result.data, status: 200 };
  } else {
    console.error('[API] uploadCompletionImage - FAILED:', result.error);
    const error = new Error(result.error);
    error.response = { data: { detail: result.error } };
    throw error;
  }
};

// Production Dispatches
export const addProductionDispatch = (data) => api.post("/production/dispatches", data);

// Scrap/Loss Declarations
export const addScrapDeclaration = (data) => api.post("/production/scrap-declarations", data);
export const getScrapDeclarations = (lotId) => api.get(`/production/scrap-declarations/${lotId}`);
export const deleteScrapDeclaration = (declarationId) => api.delete(`/production/scrap-declarations/${declarationId}`);

// Reports
export const getFactoryPendingReport = () => api.get("/production/reports/factory-pending");
export const getCompletedNotDispatched = () => api.get("/production/reports/completed-not-dispatched");
export const getFactoryHoldingReport = () => api.get("/production/reports/factory-holding");
export const getMonthlySummary = (year, month) => api.get("/production/reports/monthly-summary", { params: { year, month } });

// Factory Performance
export const getFactoryPerformance = () => api.get("/production/analytics/factory-performance");

// Sample Data
export const createSampleData = () => api.post("/production/create-sample-data");

// Production File URL (legacy - use fetchProductionImage for authenticated access)
export const getProductionFileUrl = (path) => `${API_BASE}/production/files/${path}`;

// Fetch Production Image with authentication (returns blob URL)
export const fetchProductionImage = async (path) => {
  const response = await api.get(`/production/files/${path}`, {
    responseType: 'blob'
  });
  return URL.createObjectURL(response.data);
};

// Lot Documents Upload with robust success detection
export const uploadLotDocument = async (lotId, documentType, file) => {
  console.log('[API] uploadLotDocument - Starting:', { lotId, documentType, fileName: file?.name, fileSize: file?.size });
  const formData = new FormData();
  formData.append("file", file);
  
  const result = await performUpload(`/lot-documents/${lotId}/${documentType}`, formData, 'post');
  
  if (result.success) {
    console.log('[API] uploadLotDocument - SUCCESS:', result.data);
    return { success: true, data: result.data };
  } else {
    console.error('[API] uploadLotDocument - FAILED:', result.error);
    return { success: false, error: result.error };
  }
};

// Export performUpload helper for custom upload handling
export { performUpload, isUploadSuccess };

// ===================== SALES ORDERS =====================
export const getSalesOrders = (params) => api.get("/sales-orders", { params });
export const getSalesOrder = (id) => api.get(`/sales-orders/${id}`);
export const createSalesOrder = (data) => api.post("/sales-orders", data);
export const updateSalesOrder = (id, data) => api.put(`/sales-orders/${id}`, data);
export const completeSalesOrder = (id) => api.post(`/sales-orders/${id}/complete`);
export const reopenSalesOrder = (id) => api.post(`/sales-orders/${id}/reopen`);
export const deleteSalesOrder = (id) => api.delete(`/sales-orders/${id}`);
export const getSalesOrderQualityList = () =>
  api.get("/sales-orders/qualities", { timeout: PICKER_TIMEOUT });
export const downloadSalesOrderPdf = (id) =>
  api.get(`/sales-orders/${id}/pdf`, { responseType: "blob" });
export const getSalesOrderPreview = (id) => api.get(`/sales-orders/${id}/preview`);

// ===================== CLIENT DETAILS (address + GSTIN) =====================
export const getClientDetails = (name) =>
  api.get(`/clients/${encodeURIComponent(name)}/details`);
export const updateClientDetails = (name, data) =>
  api.put(`/clients/${encodeURIComponent(name)}/details`, data);

export default api;
